import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Switch, FormControlLabel, TextField, Button, Stack,
  Alert, CircularProgress, Tooltip, IconButton, MenuItem, Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckIcon, Cancel as CancelIcon, Edit as EditIcon, Save as SaveIcon,
  MyLocation as SafeZoneIcon, Shield as ShieldIcon, Speed as SpeedIcon,
  NotificationsActive as AlertIcon, Phone as PhoneIcon, Business as BusinessIcon,
  Add as AddIcon, Delete as DeleteIcon, Star as StarIcon, StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Las opciones de un usuario de la app, para verlas y cambiarlas desde el portal.
//
// Vive aparte porque se usa en dos sitios: la ficha del cliente —tenga o no suscripción— y el
// detalle de una suscripción. Antes solo existía el segundo, así que a un cliente sin
// suscripción no se le podía tocar nada.
//
// Al guardar se manda SOLO lo que cambió, con rutas de punto (`safeZone.radius`). Mandar la
// configuración entera pisaba lo que el cliente hubiera guardado desde la app mientras la
// ficha estaba abierta.
// ─────────────────────────────────────────────────────────────────────────────

// Los valores por defecto del modelo, para un usuario que nunca abrió la app.
const POR_DEFECTO: any = {
  enableNotifications: true,
  enableAccidentDetection: false,
  enableAntiTheft: false,
  enableTowingAlert: false,
  enableVibrationAlert: false,
  enableDoorAlert: false,
  enableBiometricShutdown: false,
  shareLocationEnabled: false,
  enableSpeedAlerts: false,
  speedLimit: 80,
  maintenanceReminders: false,
  emergencyContacts: [],
  safeZone: { enabled: false, enabled_independent: false, radius: 200, autoShutdown: false, speedCheck: true },
  aggressiveDriving: { enabled: false, accelerationSensitivity: 'Media', brakingSensitivity: 'Media', corneringSensitivity: 'Media' },
  driverConfig: { enabled: false },
  scheduleControl: { enabled: false },
  idleConfig: { enabled: false },
  scheduledReports: { enabled: false },
};

const SUBDOCS = ['safeZone', 'aggressiveDriving', 'driverConfig', 'scheduleControl', 'idleConfig', 'scheduledReports'];
const PARENTESCOS = ['Padre/Madre', 'Esposo/a', 'Hijo/a', 'Hermano/a', 'Amigo/a', 'Otro'];
const SENSIBILIDADES = ['Baja', 'Media', 'Alta'];

const conDefectos = (cfg: any) => {
  const base: any = { ...POR_DEFECTO, ...(cfg || {}) };
  for (const k of SUBDOCS) base[k] = { ...POR_DEFECTO[k], ...(cfg?.[k] || {}) };
  base.emergencyContacts = Array.isArray(cfg?.emergencyContacts) ? cfg.emergencyContacts : [];
  return base;
};

/** Solo lo que cambió, con rutas de punto para los subdocumentos. */
const cambios = (original: any, borrador: any) => {
  const out: Record<string, any> = {};
  for (const k of Object.keys(POR_DEFECTO)) {
    if (SUBDOCS.includes(k)) {
      for (const sub of Object.keys({ ...POR_DEFECTO[k], ...borrador[k] })) {
        if (JSON.stringify(original[k]?.[sub]) !== JSON.stringify(borrador[k]?.[sub])) out[`${k}.${sub}`] = borrador[k][sub];
      }
    } else if (k === 'emergencyContacts') {
      const limpiar = (l: any[]) => l.map((c) => ({ name: c.name || '', phone: c.phone || '', relationship: c.relationship || '', primaryContact: !!c.primaryContact }));
      if (JSON.stringify(limpiar(original[k])) !== JSON.stringify(limpiar(borrador[k]))) out[k] = limpiar(borrador[k]);
    } else if (JSON.stringify(original[k]) !== JSON.stringify(borrador[k])) {
      out[k] = borrador[k];
    }
  }
  return out;
};

const Estado: React.FC<{ value?: boolean; label: string }> = ({ value, label }) => (
  <Chip icon={value ? <CheckIcon /> : <CancelIcon />} label={label} color={value ? 'success' : 'default'}
    variant={value ? 'filled' : 'outlined'} size="small" sx={{ m: 0.5 }} />
);

const Seccion: React.FC<{ icon: React.ReactNode; titulo: string; children: React.ReactNode }> = ({ icon, titulo, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon} {titulo}
    </Typography>
    {children}
  </Paper>
);

interface Props {
  /** Id de usuario de Traccar. */
  userId: string;
  /** Lo guardado, o null si el usuario nunca abrió la app. */
  configuracion: any | null;
  puedeEditar: boolean;
  /** En un diálogo conviene abrir directamente en modo edición. */
  siempreEditando?: boolean;
  onGuardado?: (nueva: any) => void;
}

const EditorConfiguracionUsuario: React.FC<Props> = ({ userId, configuracion, puedeEditar, siempreEditando, onGuardado }) => {
  const original = useMemo(() => conDefectos(configuracion), [configuracion]);
  const [editando, setEditando] = useState(!!siempreEditando);
  const [borrador, setBorrador] = useState<any>(original);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardadoOk, setGuardadoOk] = useState(false);

  useEffect(() => { setBorrador(original); }, [original]);

  const pendientes = useMemo(() => cambios(original, borrador), [original, borrador]);
  const hayCambios = Object.keys(pendientes).length > 0;

  const poner = (campo: string, valor: any) => setBorrador((b: any) => ({ ...b, [campo]: valor }));
  const ponerEn = (padre: string, campo: string, valor: any) =>
    setBorrador((b: any) => ({ ...b, [padre]: { ...b[padre], [campo]: valor } }));
  const ponerContacto = (i: number, campo: string, valor: any) =>
    setBorrador((b: any) => ({
      ...b,
      emergencyContacts: b.emergencyContacts.map((c: any, j: number) =>
        campo === 'primaryContact'
          ? { ...c, primaryContact: j === i ? valor : false } // un solo principal
          : j === i ? { ...c, [campo]: valor } : c),
    }));

  const radio = Number(borrador.safeZone.radius);
  const radioMal = !(radio >= 50 && radio <= 10000);
  const limiteMal = borrador.enableSpeedAlerts && !(Number(borrador.speedLimit) >= 1 && Number(borrador.speedLimit) <= 250);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setGuardadoOk(false);
    try {
      const r = await apiService.updateSubscriptionConfig(userId, pendientes);
      setGuardadoOk(true);
      if (!siempreEditando) setEditando(false);
      onGuardado?.(r?.data ?? r);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const cancelar = () => {
    setBorrador(original);
    setError(null);
    if (!siempreEditando) setEditando(false);
  };

  const interruptor = (checked: boolean, onChange: (v: boolean) => void, label: string, color?: 'warning') => (
    <FormControlLabel sx={{ mr: 2 }} label={label}
      control={<Switch size="small" color={color} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />} />
  );

  // ── Solo lectura ──
  if (!editando) {
    return (
      <Box>
        {guardadoOk && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGuardadoOk(false)}>Configuración guardada</Alert>}
        {!configuracion && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Este usuario no tiene configuración guardada (no ha abierto la app). {puedeEditar && 'Puedes configurarlo desde aquí.'}
          </Alert>
        )}
        {configuracion && (
          <>
            <Seccion icon={<SafeZoneIcon fontSize="small" color="info" />} titulo="Zona Segura">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <Estado value={original.safeZone.enabled} label="Habilitada" />
                <Estado value={original.safeZone.autoShutdown} label="Auto-Apagado" />
                <Estado value={original.safeZone.speedCheck} label="Speed Check" />
                {original.safeZone.enabled_independent && <Chip label="Concedida sin plan" color="warning" size="small" sx={{ m: 0.5 }} />}
                <Chip label={`Radio: ${original.safeZone.radius}m`} variant="outlined" size="small" sx={{ m: 0.5 }} />
              </Box>
            </Seccion>
            <Seccion icon={<ShieldIcon fontSize="small" color="error" />} titulo="Seguridad">
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Estado value={original.enableAccidentDetection} label="Detección Accidentes" />
                <Estado value={original.enableAntiTheft} label="Anti-Robo" />
                <Estado value={original.enableTowingAlert} label="Alerta Grúa" />
                <Estado value={original.enableVibrationAlert} label="Alerta Vibración" />
                <Estado value={original.enableDoorAlert} label="Alerta Puerta" />
                <Estado value={original.enableBiometricShutdown} label="Apagado Biométrico" />
                <Estado value={original.shareLocationEnabled} label="Compartir Ubicación" />
              </Box>
            </Seccion>
            <Seccion icon={<SpeedIcon fontSize="small" color="warning" />} titulo="Conductor">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <Estado value={original.enableSpeedAlerts} label="Alertas de Velocidad" />
                {original.enableSpeedAlerts && <Chip label={`Límite: ${original.speedLimit} km/h`} variant="outlined" size="small" sx={{ m: 0.5 }} />}
                <Estado value={original.maintenanceReminders} label="Recordatorios Mant." />
                <Estado value={original.enableNotifications} label="Notificaciones" />
              </Box>
            </Seccion>
            <Seccion icon={<AlertIcon fontSize="small" color="error" />} titulo="Conducción Agresiva">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <Estado value={original.aggressiveDriving.enabled} label="Habilitada" />
                {original.aggressiveDriving.enabled && (
                  <>
                    <Chip label={`Aceleración: ${original.aggressiveDriving.accelerationSensitivity}`} size="small" variant="outlined" sx={{ m: 0.5 }} />
                    <Chip label={`Frenado: ${original.aggressiveDriving.brakingSensitivity}`} size="small" variant="outlined" sx={{ m: 0.5 }} />
                    <Chip label={`Curvas: ${original.aggressiveDriving.corneringSensitivity}`} size="small" variant="outlined" sx={{ m: 0.5 }} />
                  </>
                )}
              </Box>
            </Seccion>
            <Seccion icon={<BusinessIcon fontSize="small" color="primary" />} titulo="Empresarial">
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Estado value={original.driverConfig.enabled} label="ID Conductor" />
                <Estado value={original.scheduleControl.enabled} label="Control Horario" />
                <Estado value={original.idleConfig.enabled} label="Detección Inactivo" />
                <Estado value={original.scheduledReports.enabled} label="Reportes Programados" />
              </Box>
            </Seccion>
            <Seccion icon={<PhoneIcon fontSize="small" color="error" />} titulo="Contactos de Emergencia">
              {original.emergencyContacts.length ? original.emergencyContacts.map((c: any, i: number) => (
                <Chip key={i} size="small" sx={{ m: 0.5 }} variant="outlined"
                  label={`${c.name || 'Sin nombre'}: ${c.phone}${c.relationship ? ` · ${c.relationship}` : ''}${c.primaryContact ? ' (Principal)' : ''}`} />
              )) : <Typography variant="body2" color="text.secondary">Ninguno</Typography>}
            </Seccion>
          </>
        )}
        {puedeEditar && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => { setGuardadoOk(false); setEditando(true); }}>
            {configuracion ? 'Editar configuración' : 'Configurar'}
          </Button>
        )}
      </Box>
    );
  }

  // ── Edición ──
  return (
    <Box>
      {!configuracion && (
        <Alert severity="info" sx={{ mb: 2 }}>Este usuario no tiene configuración. Se creará al guardar, con estos valores.</Alert>
      )}

      <Seccion icon={<SafeZoneIcon fontSize="small" color="info" />} titulo="Zona Segura">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {interruptor(borrador.safeZone.enabled, (v) => ponerEn('safeZone', 'enabled', v), 'Habilitada')}
          {interruptor(borrador.safeZone.autoShutdown, (v) => ponerEn('safeZone', 'autoShutdown', v), 'Auto-Apagado')}
          {interruptor(borrador.safeZone.speedCheck, (v) => ponerEn('safeZone', 'speedCheck', v), 'Speed Check')}
          <Tooltip title="Le da acceso a Zona Segura aunque su plan no la incluya. Se concede a petición: apaga el vehículo solo, así que no conviene darlo a quien no sepa usarlo.">
            <span>{interruptor(borrador.safeZone.enabled_independent, (v) => ponerEn('safeZone', 'enabled_independent', v), 'Conceder sin plan', 'warning')}</span>
          </Tooltip>
          <TextField size="small" type="number" label="Radio (m)" value={borrador.safeZone.radius}
            onChange={(e) => ponerEn('safeZone', 'radius', e.target.value === '' ? '' : Number(e.target.value))}
            error={radioMal} helperText={radioMal ? 'Entre 50 y 10,000' : undefined} sx={{ width: 130, mt: 1 }} />
        </Box>
      </Seccion>

      <Seccion icon={<ShieldIcon fontSize="small" color="error" />} titulo="Seguridad">
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {interruptor(borrador.enableAccidentDetection, (v) => poner('enableAccidentDetection', v), 'Detección Accidentes')}
          {interruptor(borrador.enableAntiTheft, (v) => poner('enableAntiTheft', v), 'Anti-Robo')}
          {interruptor(borrador.enableTowingAlert, (v) => poner('enableTowingAlert', v), 'Alerta Grúa')}
          {interruptor(borrador.enableVibrationAlert, (v) => poner('enableVibrationAlert', v), 'Alerta Vibración')}
          {interruptor(borrador.enableDoorAlert, (v) => poner('enableDoorAlert', v), 'Alerta Puerta')}
          {interruptor(borrador.enableBiometricShutdown, (v) => poner('enableBiometricShutdown', v), 'Apagado Biométrico')}
          {interruptor(borrador.shareLocationEnabled, (v) => poner('shareLocationEnabled', v), 'Compartir Ubicación')}
        </Box>
      </Seccion>

      <Seccion icon={<SpeedIcon fontSize="small" color="warning" />} titulo="Conductor">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {interruptor(borrador.enableSpeedAlerts, (v) => poner('enableSpeedAlerts', v), 'Alertas de Velocidad')}
          {borrador.enableSpeedAlerts && (
            <TextField size="small" type="number" label="Límite km/h" value={borrador.speedLimit}
              onChange={(e) => poner('speedLimit', e.target.value === '' ? '' : Number(e.target.value))}
              error={limiteMal} helperText={limiteMal ? 'Entre 1 y 250' : undefined} sx={{ width: 120, mr: 2 }} />
          )}
          {interruptor(borrador.maintenanceReminders, (v) => poner('maintenanceReminders', v), 'Recordatorios Mant.')}
          {interruptor(borrador.enableNotifications, (v) => poner('enableNotifications', v), 'Notificaciones')}
        </Box>
      </Seccion>

      <Seccion icon={<AlertIcon fontSize="small" color="error" />} titulo="Conducción Agresiva">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {interruptor(borrador.aggressiveDriving.enabled, (v) => ponerEn('aggressiveDriving', 'enabled', v), 'Habilitada')}
          {borrador.aggressiveDriving.enabled && ([
            ['accelerationSensitivity', 'Aceleración'], ['brakingSensitivity', 'Frenado'], ['corneringSensitivity', 'Curvas'],
          ] as const).map(([campo, etiqueta]) => (
            <TextField key={campo} select size="small" label={etiqueta} value={borrador.aggressiveDriving[campo]}
              onChange={(e) => ponerEn('aggressiveDriving', campo, e.target.value)} sx={{ width: 130 }}>
              {SENSIBILIDADES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          ))}
        </Box>
      </Seccion>

      <Seccion icon={<BusinessIcon fontSize="small" color="primary" />} titulo="Empresarial">
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {interruptor(borrador.driverConfig.enabled, (v) => ponerEn('driverConfig', 'enabled', v), 'ID Conductor')}
          {interruptor(borrador.scheduleControl.enabled, (v) => ponerEn('scheduleControl', 'enabled', v), 'Control Horario')}
          {interruptor(borrador.idleConfig.enabled, (v) => ponerEn('idleConfig', 'enabled', v), 'Detección Inactivo')}
          {interruptor(borrador.scheduledReports.enabled, (v) => ponerEn('scheduledReports', 'enabled', v), 'Reportes Programados')}
        </Box>
      </Seccion>

      <Seccion icon={<PhoneIcon fontSize="small" color="error" />} titulo="Contactos de Emergencia">
        <Stack spacing={1.5}>
          {borrador.emergencyContacts.map((c: any, i: number) => (
            <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <TextField size="small" label="Nombre" value={c.name || ''} onChange={(e) => ponerContacto(i, 'name', e.target.value)} sx={{ flex: '1 1 140px' }} />
              <TextField size="small" label="Teléfono" value={c.phone || ''} onChange={(e) => ponerContacto(i, 'phone', e.target.value)} sx={{ flex: '1 1 140px' }} />
              <Autocomplete freeSolo size="small" options={PARENTESCOS} value={c.relationship || ''}
                onInputChange={(_, v) => ponerContacto(i, 'relationship', v)}
                renderInput={(params) => <TextField {...params} label="Parentesco" />} sx={{ flex: '1 1 140px' }} />
              <Tooltip title={c.primaryContact ? 'Contacto principal' : 'Marcar como principal'}>
                <IconButton size="small" color={c.primaryContact ? 'warning' : 'default'} onClick={() => ponerContacto(i, 'primaryContact', !c.primaryContact)}>
                  {c.primaryContact ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Quitar">
                <IconButton size="small" color="error"
                  onClick={() => poner('emergencyContacts', borrador.emergencyContacts.filter((_: any, j: number) => j !== i))}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Box>
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => poner('emergencyContacts', [...borrador.emergencyContacts, { name: '', phone: '', relationship: '', primaryContact: borrador.emergencyContacts.length === 0 }])}>
              Agregar contacto
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            El teléfono se guarda en formato marcable (+1 y 10 dígitos). Los contactos sin teléfono se descartan al guardar.
          </Typography>
        </Stack>
      </Seccion>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {guardadoOk && siempreEditando && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGuardadoOk(false)}>Configuración guardada</Alert>}

      <Stack direction="row" spacing={1}>
        <Button variant="contained" startIcon={guardando ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={guardar} disabled={guardando || !hayCambios || radioMal || limiteMal}>
          Guardar{hayCambios ? ` (${Object.keys(pendientes).length})` : ''}
        </Button>
        <Button onClick={cancelar} disabled={guardando || (siempreEditando && !hayCambios)}>
          {siempreEditando ? 'Descartar cambios' : 'Cancelar'}
        </Button>
      </Stack>
    </Box>
  );
};

export default EditorConfiguracionUsuario;
